import { useEffect, useMemo, useRef, useState } from 'react';

import { Empty, Field, Search } from '../../components/ui/bits.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { useDebounced } from '../../hooks.js';
import { fmtDate, fullName, pct, tecajniki } from '../../lib/format.js';
import { bestExamAttempt, matchStudent } from '../../lib/progress.js';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { useSola, solaNastavitve } from '../../sola.jsx';
import { ExamDiagram, hasDiagram } from '../../components/ExamDiagram.jsx';
import { t } from '../../i18n/index.js';

/*
  Zaključni test — pogled inštruktorja.

  Dvoje: kdo ga je opravil (to je zapis, ki ga mora šola hraniti) in
  urejanje ključa pravilnih odgovorov.

  Ključ je urejiv, ker je bil prenesen iz Google obrazca, ki pravilnih
  odgovorov ni vseboval — vprašanja so bila prepisana, odgovori pa
  predlagani in jih mora Luka potrditi.
*/

export function ExamAdmin({ users, exams, onRefresh }) {
  const [view, setView] = useState('rezultati');

  return (
    <div className="stack">
      <nav className="tabs" role="tablist" aria-label={t('Zaključni test')}>
        {[
          ['rezultati', t('Rezultati')],
          ['vprasanja', t('Vprašanja in ključ')],
        ].map(([v, label]) => (
          <button
            key={v}
            className="tab"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
          >
            {label}
          </button>
        ))}
      </nav>

      {view === 'rezultati' ? (
        <Results users={users} exams={exams} onRefresh={onRefresh} />
      ) : (
        <Questions />
      )}
    </div>
  );
}

/* ── Rezultati ──────────────────────────────────────────────── */

function Results({ users, exams, onRefresh }) {
  const { toast, confirm } = useUI();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('vsi');
  const [odgovori, setOdgovori] = useState(null); // { user, attempt, rows }
  const [busyId, setBusyId] = useState(null);
  const q = useDebounced(query, 180);

  const rows = useMemo(
    () =>
      users
        .filter((u) => !u.archived && !u.testni && u.ime)
        .map((u) => {
          const attempts = exams[u.id] ?? [];
          return { u, attempts, passed: bestExamAttempt(attempts), last: attempts[0] ?? null };
        }),
    [users, exams],
  );

  const shown = rows
    .filter((r) =>
      filter === 'opravili'
        ? !!r.passed
        : filter === 'niso'
          ? !r.passed
          : true,
    )
    .filter((r) => matchStudent(r.u, q));

  const nPassed = rows.filter((r) => r.passed).length;

  const poglejOdgovore = async (attempt, u) => {
    setBusyId(attempt.id);
    try {
      const rows = await api.loadExamReview(attempt.id);
      setOdgovori({ user: u, attempt, rows });
    } catch (e) {
      toast.bad(e.message ?? t('Odgovorov ni bilo mogoče naložiti.'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (attempt, u) => {
    const ok = await confirm({
      title: t('Izbrišem ta poskus?'),
      body: `${fullName(u)} — ${fmtDate(attempt.submitted_at)}, ${attempt.score}/${attempt.total}.`,
      detail: [t('Šola mora rezultate hraniti. Briši samo očitno napačne vnose.')],
      confirmLabel: t('Izbriši'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteExamAttempt(attempt.id);
      toast.ok(t('Poskus je izbrisan.'));
      await onRefresh();
    } catch (e) {
      toast.bad(e.message ?? t('Brisanje ni uspelo.'));
    }
  };

  return (
    <div className="stack">
      <section className="card card--pad row row--wrap">
        <div className="spacer">
          <h2 className="t-lg">{t('Rezultati zaključnega testa')}</h2>
          <p className="t-sm t-dim">
            {t('Opravilo')} <strong>{nPassed}</strong> {t('od')} {rows.length} {tecajniki(rows.length)}.
          </p>
        </div>
        <Search value={query} onChange={setQuery} placeholder={t('Številka ali ime')} />
      </section>

      <div className="tabs" role="tablist" aria-label={t('Filter rezultatov')}>
        {[
          ['vsi', `${t('Vsi')} (${rows.length})`],
          ['opravili', `${t('Opravili')} (${nPassed})`],
          ['niso', `${t('Niso')} (${rows.length - nPassed})`],
        ].map(([v, label]) => (
          <button
            key={v}
            className="tab"
            role="tab"
            aria-selected={filter === v}
            onClick={() => setFilter(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="card">
          <Empty icon="search" title={t('Ni zadetkov')} />
        </div>
      ) : (
        <div className="card list">
          {shown.map(({ u, attempts, passed, last }) => (
            <div
              key={u.id}
              className="list__row"
              style={{ flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div className="row row--wrap">
                <span className="num t-sm t-black t-brand" style={{ minWidth: 32 }}>
                  #{u.stevilka ?? '—'}
                </span>
                <span className="spacer t-md t-bold">{fullName(u)}</span>

                {passed ? (
                  <span className="badge badge--ok num">
                    <Icon name="check" size={13} /> {passed.score}/{passed.total} ·{' '}
                    {fmtDate(passed.submitted_at)}
                  </span>
                ) : last ? (
                  <span className="badge badge--bad num">
                    <Icon name="x" size={13} /> {last.score}/{last.total} ·{' '}
                    {fmtDate(last.submitted_at)}
                  </span>
                ) : (
                  <span className="badge badge--mute">{t('Še ni reševal')}</span>
                )}
              </div>

              {attempts.length > 0 && (
                <details>
                  <summary className="t-xs t-dimmer" style={{ cursor: 'pointer' }}>
                    {attempts.length === 1 ? t('1 poskus') : t('{n} poskusov', { n: attempts.length })}
                  </summary>
                  <div className="stack stack--tight" style={{ marginTop: 'var(--s-2)' }}>
                    {attempts.map((a) => (
                      <div key={a.id} className="row row--wrap t-sm">
                        <span className="spacer t-dim">
                          {fmtDate(a.submitted_at)} — {a.score}/{a.total} (
                          {pct(a.score, a.total)} %, {t('prag')} {a.prag} %)
                        </span>
                        {a.podpis && <span className="t-xs t-dimmer">{t('podpis:')} {a.podpis}</span>}
                        <button
                          className="btn btn--sm btn--secondary"
                          onClick={() => poglejOdgovore(a, u)}
                          disabled={busyId === a.id}
                        >
                          {busyId === a.id ? t('Nalagam …') : t('Poglej odgovore')}
                        </button>
                        <button
                          className="btn-icon btn-icon--sm"
                          onClick={() => remove(a, u)}
                          aria-label={t('Izbriši poskus')}
                        >
                          <Icon name="x" size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {odgovori && <OdgovoriModal {...odgovori} onClose={() => setOdgovori(null)} />}
    </div>
  );
}

/* ── Oddani odgovori enega poskusa ──────────────────────────
   Inštruktor mora videti, kaj je kdo obkljukal — ne le izida. Brez tega
   ne more presoditi, ali je vprašanje slabo zastavljeno ali ključ napačen.
   (Pri 4. vprašanju se je izkazalo ravno to.)                          */

function OdgovoriModal({ user, attempt, rows, onClose }) {
  const napacna = rows.filter((r) => !r.is_correct);

  return (
    <Modal
      eyebrow={`${fmtDate(attempt.submitted_at)} · ${attempt.score}/${attempt.total} · ${t('prag')} ${attempt.prag} %`}
      title={t('Odgovori — {name}', { name: fullName(user) })}
      onClose={onClose}
      footer={
        <button className="btn btn--primary" onClick={onClose} data-autofocus>
          {t('Zapri')}
        </button>
      }
    >
      <div className="stack">
        <div className="row row--wrap">
          <span className={`badge ${attempt.passed ? 'badge--ok' : 'badge--bad'}`}>
            {attempt.passed ? t('Opravljen') : t('Ni opravljen')}
          </span>
          <span className="badge badge--mute num">
            {napacna.length} {napacna.length === 1 ? t('napaka') : t('napak')}
          </span>
          {attempt.podpis && (
            <span className="t-xs t-dimmer">{t('podpis:')} {attempt.podpis}</span>
          )}
        </div>

        <ol className="stack" style={{ listStyle: 'none' }}>
          {rows.map((r) => (
            <li
              key={r.id}
              className="card card--pad stack stack--tight"
              style={
                r.is_correct
                  ? undefined
                  : { borderColor: 'var(--bad-border)', background: 'var(--bad-wash)' }
              }
            >
              <span className="row row--top" style={{ gap: 'var(--s-2)' }}>
                <Icon
                  name={r.is_correct ? 'check' : 'x'}
                  size={16}
                  className={r.is_correct ? 't-ok' : 't-bad'}
                />
                <span className="spacer t-md t-bold">
                  <span className="t-brand num">{r.ord}.</span> {r.question}
                </span>
              </span>

              {r.options.map((opt, i) => {
                const izbral = r.chosen === i;
                const pravilen = r.correct === i;
                return (
                  <span
                    key={i}
                    className="row t-sm"
                    style={{
                      gap: 'var(--s-2)',
                      color: pravilen
                        ? 'var(--ok-text)'
                        : izbral
                          ? 'var(--bad-text)'
                          : 'var(--text-3)',
                      fontWeight: izbral || pravilen ? 700 : 400,
                    }}
                  >
                    <span style={{ width: 62, flexShrink: 0 }}>
                      {pravilen ? t('pravilno') : izbral ? t('izbral') : ''}
                    </span>
                    <span className="spacer">{opt}</span>
                  </span>
                );
              })}

              {r.chosen == null && (
                <span className="t-sm t-bad">{t('Na to vprašanje ni odgovoril.')}</span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </Modal>
  );
}

/* ── Vprašanja in ključ ─────────────────────────────────────── */

function Questions() {
  const { toast } = useUI();
  const { sola } = useSola();
  const [questions, setQuestions] = useState(null);
  // Prag je nastavitev šole (Šola → Nastavitve), ne klic v bazo.
  const threshold = solaNastavitve(sola).prag;

  useEffect(() => {
    api
      .loadExamQuestionsAdmin(sola.id)
      .then(setQuestions)
      .catch((e) => toast.bad(e.message ?? t('Vprašanj ni bilo mogoče naložiti.')));
  }, [toast, sola.id]);

  const setCorrect = async (q, index) => {
    const prev = questions;
    setQuestions((list) => list.map((x) => (x.id === q.id ? { ...x, correct: index } : x)));
    try {
      await api.saveExamQuestion(q.id, { correct: index });
      toast.ok(t('Pravilni odgovor je shranjen.'));
    } catch (e) {
      setQuestions(prev);
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    }
  };

  const saveRazlaga = async (q, text) => {
    if ((q.razlaga ?? '') === text) return;
    const prev = questions;
    setQuestions((list) => list.map((x) => (x.id === q.id ? { ...x, razlaga: text } : x)));
    try {
      await api.saveExamQuestion(q.id, { razlaga: text || null });
      toast.ok(t('Razlaga je shranjena.'));
    } catch (e) {
      setQuestions(prev);
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    }
  };

  const uploadSlika = async (q, file) => {
    if (!file) return;
    try {
      const url = await api.uploadExamImage(sola.id, q.ord, file);
      await api.saveExamQuestion(q.id, { slika_url: url });
      setQuestions((list) => list.map((x) => (x.id === q.id ? { ...x, slika_url: url } : x)));
      toast.ok(t('Slika je naložena.'));
    } catch (e) {
      toast.bad(e.message ?? t('Nalaganje slike ni uspelo.'));
    }
  };

  const removeSlika = async (q) => {
    try {
      await api.saveExamQuestion(q.id, { slika_url: null });
      setQuestions((list) => list.map((x) => (x.id === q.id ? { ...x, slika_url: null } : x)));
      toast.ok(t('Slika je odstranjena.'));
    } catch (e) {
      toast.bad(e.message ?? t('Odstranjevanje ni uspelo.'));
    }
  };

  const toggleActive = async (q) => {
    const prev = questions;
    setQuestions((list) => list.map((x) => (x.id === q.id ? { ...x, active: !x.active } : x)));
    try {
      await api.saveExamQuestion(q.id, { active: !q.active });
    } catch (e) {
      setQuestions(prev);
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    }
  };

  if (questions === null) {
    return (
      <div className="card card--pad" aria-busy="true">
        <p className="t-md t-dim" role="status">
          {t('Nalagam vprašanja …')}
        </p>
      </div>
    );
  }

  const active = questions.filter((q) => q.active).length;

  return (
    <div className="stack">
      <section className="card card--brand card--pad">
        <h2 className="t-lg">{t('Vprašanja in ključ')}</h2>
        <p className="t-sm t-dim" style={{ marginTop: 'var(--s-1)' }}>
          {t('Vprašanja so prenesena iz Google obrazca. Ta pravilnih odgovorov ni vseboval, zato je')}{' '}
          <strong>{t('ključ predlagan')}</strong>{' '}
          {t('— preveri ga in po potrebi popravi. Zeleno obrobljena možnost je trenutno pravilna.')}
        </p>
        <p className="t-sm t-dim" style={{ marginTop: 'var(--s-2)' }}>
          {t('Aktivnih vprašanj:')} <strong className="num">{active}</strong> · {t('za opravljeno je potrebnih')}{' '}
          <strong className="num">{threshold} %</strong>.
        </p>
        <p className="t-sm t-dim" style={{ marginTop: 'var(--s-2)' }}>
          {t('Razlaga in slika se tečajniku pokažeta')} <strong>{t('samo, če je vprašanje zgrešil')}</strong>{' '}
          {t('— med reševanjem ne, ker bi slika lahko odgovor izdala. Razlage so osnutki; popravi jih po svoje.')}
        </p>
      </section>

      <ol className="stack" style={{ listStyle: 'none' }}>
        {questions.map((q, i) => (
          <li key={q.id} className="card card--pad" style={{ opacity: q.active ? 1 : 0.55 }}>
            <div className="row row--top" style={{ marginBottom: 'var(--s-3)' }}>
              <span className="t-md t-bold spacer">
                <span className="t-brand num">{i + 1}.</span> {q.question}
              </span>
              <button className="btn btn--sm btn--ghost" onClick={() => toggleActive(q)}>
                {q.active ? t('Izključi') : t('Vključi')}
              </button>
            </div>

            <div className="stack stack--tight">
              {q.options.map((opt, oi) => {
                const isCorrect = q.correct === oi;
                return (
                  <label key={oi} className={`choice${isCorrect ? ' choice--correct' : ''}`}>
                    <input
                      type="radio"
                      name={`k-${q.id}`}
                      className="checkbox"
                      style={{ borderRadius: '50%' }}
                      checked={isCorrect}
                      onChange={() => setCorrect(q, oi)}
                    />
                    <span className={`spacer t-md${isCorrect ? ' t-ok t-bold' : ''}`}>{opt}</span>
                  </label>
                );
              })}
            </div>

            <label className="field" style={{ marginTop: 'var(--s-3)' }}>
              <span className="field__label">{t('Razlaga ob napačnem odgovoru')}</span>
              <textarea
                className="input"
                rows={3}
                defaultValue={q.razlaga ?? ''}
                placeholder={t('Zakaj je pravilen prav ta odgovor …')}
                onBlur={(e) => saveRazlaga(q, e.target.value.trim())}
              />
            </label>

            <SlikaField q={q} onUpload={uploadSlika} onRemove={removeSlika} />
          </li>
        ))}
      </ol>

      <NovoVprasanje
        solaId={sola.id}
        nextOrd={questions.reduce((m, q) => Math.max(m, q.ord), 0) + 1}
        onAdded={(q) => setQuestions((l) => [...l, q])}
      />
    </div>
  );
}

/* ── Novo vprašanje ─────────────────────────────────────────
   Nova šola začne brez vprašanj; Perut dodaja nova po potrebi. Ključ je
   izbran takoj, da vprašanje nikoli ne obstaja brez pravilnega odgovora. */

function NovoVprasanje({ solaId, nextOrd, onAdded }) {
  const { toast } = useUI();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState(['', '', '']);
  const [correct, setCorrect] = useState(0);
  const [razlaga, setRazlaga] = useState('');
  const [busy, setBusy] = useState(false);

  const valid = q.trim().length > 3 && opts.filter((o) => o.trim()).length >= 2 && opts[correct]?.trim();

  const save = async () => {
    setBusy(true);
    try {
      const options = opts.map((o) => o.trim()).filter(Boolean);
      const idx = opts.slice(0, correct).filter((o) => o.trim()).length;
      const row = await api.addExamQuestion(solaId, {
        ord: nextOrd,
        question: q.trim(),
        options,
        correct: idx,
        razlaga: razlaga.trim() || null,
        active: true,
      });
      onAdded(row);
      setQ('');
      setOpts(['', '', '']);
      setCorrect(0);
      setRazlaga('');
      setOpen(false);
      toast.ok(t('Vprašanje je dodano.'));
    } catch (e) {
      toast.bad(e.message ?? t('Vprašanja ni bilo mogoče dodati.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn--secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(true)}>
        <Icon name="plus" size={16} /> {t('Dodaj vprašanje')}
      </button>
    );
  }

  return (
    <section className="card card--pad stack" aria-label={t('Novo vprašanje')}>
      <h3 className="t-base">{t('Novo vprašanje')} ({nextOrd}.)</h3>
      <Field label={t('Vprašanje')} id="nq-q" required>
        <input id="nq-q" className="input" value={q} onChange={(e) => setQ(e.target.value)} />
      </Field>
      <div className="stack stack--tight">
        <span className="field__label">{t('Možni odgovori — označi pravilnega')}</span>
        {opts.map((o, i) => (
          <label key={i} className={`choice${correct === i ? ' choice--correct' : ''}`}>
            <input
              type="radio"
              name="nq-correct"
              className="checkbox"
              style={{ borderRadius: '50%' }}
              checked={correct === i}
              onChange={() => setCorrect(i)}
              aria-label={t('Pravilen je odgovor {n}', { n: i + 1 })}
            />
            <input
              className="input spacer"
              value={o}
              placeholder={t('Odgovor {n}', { n: i + 1 })}
              aria-label={t('Odgovor {n}', { n: i + 1 })}
              onChange={(e) => setOpts((l) => l.map((x, j) => (j === i ? e.target.value : x)))}
            />
          </label>
        ))}
        {opts.length < 5 && (
          <button className="btn btn--sm btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setOpts((l) => [...l, ''])}>
            <Icon name="plus" size={14} /> {t('Še en odgovor')}
          </button>
        )}
      </div>
      <Field label={t('Razlaga ob napačnem odgovoru')} id="nq-r" hint={t('Neobvezno. Pokaže se tečajniku, ki vprašanje zgreši.')}>
        <textarea id="nq-r" className="input" rows={2} value={razlaga} onChange={(e) => setRazlaga(e.target.value)} />
      </Field>
      <div className="row">
        <button className="btn btn--primary" onClick={save} disabled={!valid || busy}>
          {busy ? t('Shranjujem …') : t('Shrani vprašanje')}
        </button>
        <button className="btn btn--ghost" onClick={() => setOpen(false)}>
          {t('Prekliči')}
        </button>
      </div>
    </section>
  );
}

/** Slika, ki ponazori pravilno izvedbo. Pokaže se ob razlagi. */
function SlikaField({ q, onUpload, onRemove }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    setBusy(true);
    await onUpload(q, file);
    setBusy(false);
    if (input.current) input.current.value = '';
  };

  return (
    <div className="field" style={{ marginTop: 'var(--s-3)' }}>
      <span className="field__label">{t('Slika (neobvezno)')}</span>

      {q.slika_url ? (
        <img
          src={q.slika_url}
          alt={t('Ponazoritev: {q}', { q: q.question })}
          style={{
            maxHeight: 200,
            width: 'auto',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)',
          }}
        />
      ) : hasDiagram(q.ord) ? (
        <div style={{ maxWidth: 320 }}>
          <ExamDiagram ord={q.ord} title={q.question} />
          <span className="field__hint">{t('Vgrajena shema. Fotografija jo povozi.')}</span>
        </div>
      ) : null}

      <div className="row" style={{ gap: 'var(--s-2)' }}>
        <button
          className="btn btn--sm btn--secondary"
          onClick={() => input.current?.click()}
          disabled={busy}
        >
          <Icon name="upload" size={15} />
          {busy ? t('Nalagam …') : q.slika_url ? t('Zamenjaj sliko') : t('Naloži sliko')}
        </button>

        {q.slika_url && (
          <button className="btn btn--sm btn--ghost" onClick={() => onRemove(q)}>
            {t('Odstrani')}
          </button>
        )}

        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          aria-label={t('Slika za vprašanje {n}', { n: q.ord })}
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </div>

      <span className="field__hint">
        {t('Pokaže se tečajniku ob razlagi, kadar vprašanje zgreši.')}
        {!q.slika_url && !hasDiagram(q.ord) && t(' Za to vprašanje sheme ni.')}
      </span>
    </div>
  );
}
